import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { MaintenanceGuard } from "@/components/MaintenanceGuard";

// Eager load - critical for initial paint
import Index from "./pages/Index";

// Lazy load - secondary pages (code splitting)
const ArticlePage = lazy(() => import("./pages/ArticlePage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const AccessDeniedPage = lazy(() => import("./pages/AccessDeniedPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const QuizPage = lazy(() => import("./pages/QuizPage"));
const PrivacidadPage = lazy(() => import("./pages/PrivacidadPage"));
const SobreNosotrosPage = lazy(() => import("./pages/SobreNosotrosPage"));
const AvisoLegalPage = lazy(() => import("./pages/AvisoLegalPage"));
const AfiliadosPage = lazy(() => import("./pages/AfiliadosPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const ComoElegirPage = lazy(() => import("./pages/ComoElegirPage"));
const CategoriasPage = lazy(() => import("./pages/CategoriasPage"));
const CategoriaPage = lazy(() => import("./pages/CategoriaPage"));
const GuiasPage = lazy(() => import("./pages/GuiasPage"));
const BuscarPage = lazy(() => import("./pages/BuscarPage"));
const ProductosPage = lazy(() => import("./pages/ProductosPage"));

// Lazy load - Admin pages (rarely accessed, heavy bundle)
const AdminLayout = lazy(() => import("./components/admin/AdminLayout").then(m => ({ default: m.AdminLayout })));
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const ProductsList = lazy(() => import("./pages/admin/ProductsList"));
const ProductForm = lazy(() => import("./pages/admin/ProductForm"));
const ProductBatchImport = lazy(() => import("./pages/admin/ProductBatchImport"));
const ArticlesList = lazy(() => import("./pages/admin/ArticlesList"));
const ArticleForm = lazy(() => import("./pages/admin/ArticleForm"));
const ArticleImageGenerator = lazy(() => import("./pages/admin/ArticleImageGenerator"));
const CategoriesList = lazy(() => import("./pages/admin/CategoriesList"));
const CategoryForm = lazy(() => import("./pages/admin/CategoryForm"));

// Lazy load - Quiz Admin pages
const QuizSupplementsList = lazy(() => import("./pages/admin/quiz/QuizSupplementsList"));
const QuizSupplementForm = lazy(() => import("./pages/admin/quiz/QuizSupplementForm"));
const QuizQuestionsList = lazy(() => import("./pages/admin/quiz/QuizQuestionsList"));
const QuizQuestionForm = lazy(() => import("./pages/admin/quiz/QuizQuestionForm"));
const QuizRulesList = lazy(() => import("./pages/admin/quiz/QuizRulesList"));
const QuizRuleForm = lazy(() => import("./pages/admin/quiz/QuizRuleForm"));
const QuizTagsList = lazy(() => import("./pages/admin/quiz/QuizTagsList"));
const QuizTagForm = lazy(() => import("./pages/admin/quiz/QuizTagForm"));
const QuizContraindicationsList = lazy(() => import("./pages/admin/quiz/QuizContraindicationsList"));

const queryClient = new QueryClient();

// Minimal loading fallback - doesn't block paint
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public Routes - Protected by MaintenanceGuard */}
              <Route path="/" element={<MaintenanceGuard><Index /></MaintenanceGuard>} />
              <Route path="/articulo/:slug" element={<MaintenanceGuard><ArticlePage /></MaintenanceGuard>} />
              <Route path="/quiz-sillas" element={<MaintenanceGuard><QuizPage /></MaintenanceGuard>} />
              {/* Legacy route removed - /quiz-suplementos no longer exists */}
              <Route path="/privacidad" element={<MaintenanceGuard><PrivacidadPage /></MaintenanceGuard>} />
              <Route path="/sobre-nosotros" element={<MaintenanceGuard><SobreNosotrosPage /></MaintenanceGuard>} />
              <Route path="/aviso-legal" element={<MaintenanceGuard><AvisoLegalPage /></MaintenanceGuard>} />
              <Route path="/afiliados" element={<MaintenanceGuard><AfiliadosPage /></MaintenanceGuard>} />
              <Route path="/faq" element={<MaintenanceGuard><FAQPage /></MaintenanceGuard>} />
              <Route path="/como-elegir" element={<MaintenanceGuard><ComoElegirPage /></MaintenanceGuard>} />
              <Route path="/categorias" element={<MaintenanceGuard><CategoriasPage /></MaintenanceGuard>} />
              <Route path="/categoria/:slug" element={<MaintenanceGuard><CategoriaPage /></MaintenanceGuard>} />
              <Route path="/guias" element={<MaintenanceGuard><GuiasPage /></MaintenanceGuard>} />
              <Route path="/buscar" element={<MaintenanceGuard><BuscarPage /></MaintenanceGuard>} />
              <Route path="/productos" element={<MaintenanceGuard><ProductosPage /></MaintenanceGuard>} />
              {/* Auth routes - always accessible */}
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/access-denied" element={<AccessDeniedPage />} />
              
              {/* Admin Routes - Protected by AdminLayout auth checks */}
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<Dashboard />} />
                <Route path="products" element={<ProductsList />} />
                <Route path="products/new" element={<ProductForm />} />
                <Route path="products/batch" element={<ProductBatchImport />} />
                <Route path="products/:id" element={<ProductForm />} />
                <Route path="articles" element={<ArticlesList />} />
                <Route path="articles/new" element={<ArticleForm />} />
                <Route path="articles/:id" element={<ArticleForm />} />
                <Route path="articles/images" element={<ArticleImageGenerator />} />
                <Route path="categories" element={<CategoriesList />} />
                <Route path="categories/new" element={<CategoryForm />} />
                <Route path="categories/:id" element={<CategoryForm />} />
              {/* Quiz Admin Routes */}
                <Route path="quiz/models" element={<QuizSupplementsList />} />
                <Route path="quiz/models/new" element={<QuizSupplementForm />} />
                <Route path="quiz/models/:id" element={<QuizSupplementForm />} />
                {/* Legacy redirects for compatibility */}
                <Route path="quiz/supplements" element={<Navigate to="/admin/quiz/models" replace />} />
                <Route path="quiz/supplements/new" element={<Navigate to="/admin/quiz/models/new" replace />} />
                <Route path="quiz/supplements/:id" element={<Navigate to="/admin/quiz/models" replace />} />
                <Route path="quiz/questions" element={<QuizQuestionsList />} />
                <Route path="quiz/questions/new" element={<QuizQuestionForm />} />
                <Route path="quiz/questions/:id" element={<QuizQuestionForm />} />
                <Route path="quiz/rules" element={<QuizRulesList />} />
                <Route path="quiz/rules/new" element={<QuizRuleForm />} />
                <Route path="quiz/rules/:id" element={<QuizRuleForm />} />
                <Route path="quiz/tags" element={<QuizTagsList />} />
                <Route path="quiz/tags/new" element={<QuizTagForm />} />
                <Route path="quiz/tags/:id" element={<QuizTagForm />} />
                <Route path="quiz/contraindications" element={<QuizContraindicationsList />} />
                <Route path="quiz/contraindications/new" element={<QuizContraindicationsList />} />
                <Route path="quiz/contraindications/:id" element={<QuizContraindicationsList />} />
              </Route>
              
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
